import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import config from "lib/config"
import { features, type Features } from "lib/table"
import { Settings } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route } from "routes/_base/_auth/backoffice/jobSearch/route"
import { toast } from "sonner"
import { useGetBackofficeJobNamesQuery } from "../../hooks/jobSearch/useGetBackofficeJobNamesQuery"
import { useGetBackofficeJobSearchQuery } from "../../hooks/jobSearch/useGetBackofficeJobSearchQuery"
import { useRetryBackofficeJobsMutation } from "../../hooks/jobSearch/useRetryBackofficeJobsMutation"
import type { BackofficeJobSearchResult } from "../../lib/jobSearch/getBackofficeJobSearchQuery"

const QUEUES = ["CONVERSATION", "REALTIME", "BACKGROUND", "CAMPAIGN", "SERIAL"] as const
const STATUSES = ["active", "waiting", "delayed", "failed", "completed", "paused"] as const

const STATUS_VARIANTS: Record<
  string,
  "default" | "destructive" | "secondary" | "success" | "outline"
> = {
  failed: "destructive",
  completed: "success",
  active: "default",
  waiting: "secondary",
  delayed: "outline",
  paused: "secondary",
}

const columnHelper = createColumnHelper<Features, BackofficeJobSearchResult>()

export default function BackofficeJobSearch() {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const urlSearch = Route.useSearch()

  const [selectedName, setSelectedName] = useState<string>(urlSearch.name ?? "all")
  const [queue, setQueue] = useState<(typeof QUEUES)[number] | "all">(urlSearch.queue ?? "all")
  const [status, setStatus] = useState<(typeof STATUSES)[number] | "all">(urlSearch.status ?? "all")
  const [isAdvancedMode, setIsAdvancedMode] = useState(urlSearch.advanced ?? false)
  const [dataQuery, setDataQuery] = useState(urlSearch.dataQuery ?? "")

  const isDataQueryActive = isAdvancedMode && dataQuery.trim().length > 0

  const { data: jobNames, isLoading: isLoadingNames } = useGetBackofficeJobNamesQuery()

  const retryMutation = useRetryBackofficeJobsMutation()

  const isSearched = urlSearch.searched === true
  const {
    data: jobs,
    isFetching,
    refetch,
  } = useGetBackofficeJobSearchQuery({
    query: {
      name: urlSearch.name,
      dataQuery: urlSearch.dataQuery,
      queue: urlSearch.queue,
      status: urlSearch.status,
    },
    enabled: isSearched,
  })

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("id", {
          header: t("backoffice.jobSearch.table.id"),
          cell: (props) => (
            <a
              href={`${config.apiEndpoint}/queues/queue/${encodeURIComponent(props.row.original.queue)}/${encodeURIComponent(props.getValue())}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-primary hover:underline"
            >
              {props.getValue()}
            </a>
          ),
        }),
        columnHelper.accessor("name", {
          header: t("backoffice.jobSearch.table.name"),
          meta: { colSpan: 2 },
          cell: (props) => <span className="font-mono text-xs">{props.getValue()}</span>,
        }),
        columnHelper.accessor("queue", {
          header: t("backoffice.jobSearch.table.queue"),
        }),
        columnHelper.accessor("status", {
          header: t("backoffice.jobSearch.table.status"),
          cell: (props) => (
            <Badge variant={STATUS_VARIANTS[props.getValue()] ?? "default"}>
              {props.getValue()}
            </Badge>
          ),
        }),
        columnHelper.accessor("attemptsMade", {
          header: t("backoffice.jobSearch.table.attemptsMade"),
          cell: (props) => (
            <span className="text-xs text-muted-foreground">{props.getValue()}</span>
          ),
        }),
        columnHelper.accessor("timestamp", {
          header: t("backoffice.jobSearch.table.timestamp"),
          cell: (props) => (
            <span className="text-xs text-muted-foreground">
              {new Date(props.getValue()).toLocaleString()}
            </span>
          ),
        }),
        columnHelper.accessor("failedReason", {
          header: t("backoffice.jobSearch.table.failedReason"),
          meta: { colSpan: 2 },
          cell: (props) => {
            const value = props.getValue()
            if (!value) return <span className="text-xs text-muted-foreground">{"—"}</span>
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground truncate max-w-xs block cursor-default">
                      {value}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm text-left whitespace-pre-line">
                    {value}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: jobs ?? [],
    columns,
    features,
  })

  const handleSearch = () => {
    const resolvedName = isDataQueryActive || selectedName === "all" ? undefined : selectedName
    const newSearch = {
      name: resolvedName,
      dataQuery: isDataQueryActive ? dataQuery.trim() : undefined,
      queue: queue === "all" ? undefined : queue,
      status: status === "all" ? undefined : status,
      advanced: isAdvancedMode ? true : undefined,
      searched: true,
    }
    const paramsUnchanged =
      isSearched &&
      newSearch.name === urlSearch.name &&
      newSearch.dataQuery === urlSearch.dataQuery &&
      newSearch.queue === urlSearch.queue &&
      newSearch.status === urlSearch.status

    if (paramsUnchanged) {
      refetch()
    } else {
      navigate({ search: newSearch })
    }
  }

  function renderContent() {
    if (isFetching) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (!jobs || jobs.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.jobSearch.noResults")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={`${row.original.queue}-${row.original.id}`}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <div className="space-y-6">
      <Section
        title={t("backoffice.jobSearch.title")}
        description={t("backoffice.jobSearch.description")}
      />

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={selectedName} onValueChange={setSelectedName} disabled={isDataQueryActive}>
          <SelectTrigger className="w-72">
            <SelectValue placeholder={t("backoffice.jobSearch.selectName")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("backoffice.jobSearch.selectName")}</SelectItem>
            {isLoadingNames && (
              <SelectItem value="__loading__" disabled>
                {t("backoffice.jobSearch.loadingNames")}
              </SelectItem>
            )}
            {jobNames?.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={queue}
          onValueChange={(val) => setQueue(val as (typeof QUEUES)[number] | "all")}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t("backoffice.jobSearch.allQueues")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("backoffice.jobSearch.allQueues")}</SelectItem>
            {QUEUES.map((q) => (
              <SelectItem key={q} value={q}>
                {q}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(val) => setStatus(val as (typeof STATUSES)[number] | "all")}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t("backoffice.jobSearch.allStatuses")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("backoffice.jobSearch.allStatuses")}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdvancedMode && (
          <Input
            wrapperClassName="w-auto"
            className="w-72"
            placeholder={t("backoffice.jobSearch.dataQueryPlaceholder")}
            value={dataQuery}
            onChange={(e) => setDataQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        )}
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setIsAdvancedMode((prev) => !prev)
            setDataQuery("")
          }}
          className={isAdvancedMode ? "border-primary text-primary" : ""}
        >
          <Settings className="h-4 w-4" />
        </Button>
        <Button onClick={handleSearch} disabled={isFetching}>
          {isFetching ? t("backoffice.jobSearch.searching") : t("backoffice.jobSearch.search")}
        </Button>
      </div>

      {urlSearch.status === "failed" && jobs && jobs.length > 0 && (
        <div>
          <Button
            variant="destructive"
            onClick={() =>
              retryMutation.mutate(
                {
                  name: urlSearch.name,
                  dataQuery: urlSearch.dataQuery,
                  queue: urlSearch.queue,
                  status: urlSearch.status,
                },
                {
                  onSuccess: (data) =>
                    toast.success(t("backoffice.jobSearch.retrySuccess", { count: data.retried })),
                },
              )
            }
            disabled={retryMutation.isPending}
          >
            {t("backoffice.jobSearch.retryAll")}
          </Button>
        </div>
      )}

      {isSearched && (
        <Card>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} {...header.column.columnDef.meta}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>{renderContent()}</TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}
